// CustomMailchimpHeaderMappingOverviewModal.jsx
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import {
  Dialog,
  DialogContent,
  Typography,
  Slide,
  CircularProgress,
  Collapse,
  Chip,
} from "@material-ui/core";
import {
  Close,
  ExpandMore,
  ExpandLess,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add,
} from "@material-ui/icons";

import MaterialButton from "../../ MaterialButton/MaterialButton";
import { formDataWithApiKey } from "../../../helpers";

import {
  deleteCustomHeaderMappings,
  getHeaderMapping,
} from "../../../store/middlewares/toolsMiddleware";
import ConfirmationModal from "../../../components/modals/ConfirmationModal/ConfirmationModal";
import MailchimpIcon from "../../../assets/img/mailchimp/mailchimp-small.png";
import CcbIcon from "../../../assets/img/ccb/ccb-small.png";
import "../../../styles/components/headerMapping.scss";
import { clearDeleteCustomMCHeaderMapping } from "../../../store/actions/toolsActions";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// update these routes to your actual paths
const ROUTES = {
  EDITOR: "/create-custom-mc-header-mapping",
};

const CustomMailchimpHeaderMappingOverviewModal = (props) => {
  const {
    history,

    // actions
    getHeaderMapping,
    deleteCustomHeaderMappings,
    clearDeleteCustomMCHeaderMapping,

    // redux props
    audienceWithCustomHeaderMappings,
    headerMappingStatus,
    deleteCustomHeaderMappingStatus,
  } = props;

  const [expanded, setExpanded] = useState({}); // { [listId]: true/false }
  const [deleteTarget, setDeleteTarget] = useState(null); // { listId, audienceName }
  const isLoading = headerMappingStatus === "loading";

  const isDeleting = deleteCustomHeaderMappingStatus === "loading";

  const handleClose = useCallback(() => {
    history.goBack();
  }, [history]);

  const goToEditor = useCallback(
    (listId) => {
      history.push(ROUTES.EDITOR, { audienceId: listId });
    },
    [history]
  );

  const goToAddMore = useCallback(() => {
    // editor should open with no audience selected
    history.push(ROUTES.EDITOR);
  }, [history]);

  useEffect(() => {
    const formData = formDataWithApiKey();
    getHeaderMapping(formData);
  }, [getHeaderMapping]);

  const rows = useMemo(() => {
    if (!Array.isArray(audienceWithCustomHeaderMappings)) return [];

    return audienceWithCustomHeaderMappings
      .map((x) => ({
        listId: x?.listId,
        audienceName: x?.audienceName || x?.listId,
        count: Array.isArray(x?.customHeaderMapping)
          ? x.customHeaderMapping.length
          : 0,
        mappings: Array.isArray(x?.customHeaderMapping)
          ? x.customHeaderMapping
          : [],
      }))
      .filter((r) => r.listId);
  }, [audienceWithCustomHeaderMappings]);

  const toggleExpanded = useCallback((listId) => {
    setExpanded((prev) => ({ ...prev, [listId]: !prev[listId] }));
  }, []);

  const openDeleteConfirm = useCallback((row) => {
    setDeleteTarget({ listId: row.listId, audienceName: row.audienceName });
  }, []);

  const closeDeleteConfirm = useCallback(() => {
    if (isDeleting) return;
    setDeleteTarget(null);
  }, [isDeleting]);

  const confirmDelete = useCallback(() => {
    if (!deleteTarget?.listId) return;

    const formData = formDataWithApiKey();
    // IMPORTANT: keep key aligned with your reducer logic:
    // reducer expects id
    formData.append("id", deleteTarget.listId);

    deleteCustomHeaderMappings(formData);
  }, [deleteCustomHeaderMappings, deleteTarget]);

  // Close modal after successful delete (row will be removed by reducer)
  useEffect(() => {
    if (deleteCustomHeaderMappingStatus === "success") {
      setDeleteTarget(null);
      clearDeleteCustomMCHeaderMapping();
    }
  }, [clearDeleteCustomMCHeaderMapping, deleteCustomHeaderMappingStatus]);

  return (
    <>
      <Dialog
        open={true}
        onClose={handleClose}
        TransitionComponent={Transition}
        classes={{ paper: "w-100" }}
        maxWidth="md"
      >
        <DialogContent>
          {/* Header */}
          <div className="c-mc-hm__header">
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="h5">Custom Header Mapping</Typography>

                <Chip
                  label="BETA"
                  size="small"
                  style={{
                    backgroundColor: "#2db8e8",
                    color: "#fff",
                    fontWeight: 400,
                    height: 22,
                  }}
                />
              </div>
              <Typography variant="body2" className="text-muted">
                A list of all audiences that currently have mappings.
              </Typography>
            </div>

            <Close className="close-icon mr-2" onClick={handleClose} />
          </div>

          {/* Body */}
          <div className="mt-4">
            {isLoading ? (
              <div
                className="d-flex align-items-center justify-content-center mt-4"
                style={{ minHeight: 140 }}
              >
                <CircularProgress />
              </div>
            ) : rows.length === 0 ? (
              <div className="mt-4">
                <Typography variant="body2" className="text-muted">
                  No audiences have mappings yet.
                </Typography>

                <div className="mt-4 flexer mb-4">
                  <MaterialButton
                    title={
                      <span>
                        <Add /> Add More Merge Fields
                      </span>
                    }
                    variant="contained"
                    className="bg-yellow w-25"
                    onClick={goToAddMore}
                  />
                </div>
              </div>
            ) : (
              <>
                {/* Overview Table */}
                <div className="c-mc-hm__overview-table mt-2">
                  <div
                    className="d-flex"
                    style={{
                      fontWeight: 600,
                      padding: "10px 12px",
                      borderBottom: "1px solid #e6e6e6",
                    }}
                  >
                    <div style={{ flex: "0 0 58%" }}>Audience Name</div>
                    <div style={{ flex: "0 0 22%", textAlign: "center" }}>
                      # of Mapped Fields
                    </div>
                    <div style={{ flex: "0 0 20%", textAlign: "center" }}>
                      Actions
                    </div>
                  </div>

                  {rows.map((r) => {
                    const isOpen = Boolean(expanded[r.listId]);
                    return (
                      <div key={r.listId}>
                        {/* Row */}
                        <div
                          className="d-flex align-items-center"
                          style={{
                            padding: "12px 12px",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <div style={{ flex: "0 0 58%", minWidth: 0 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: 600,
                                  minWidth: 0,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={r.audienceName}
                              >
                                {r.audienceName}
                              </span>

                              {/* small expand/collapse control */}
                              <span
                                style={{
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  fontSize: 12,
                                  opacity: 0.85,
                                  flexShrink: 0,
                                }}
                                onClick={() => toggleExpanded(r.listId)}
                                title={
                                  isOpen ? "Hide mappings" : "View mappings"
                                }
                              >
                                {isOpen ? (
                                  <>
                                    <ExpandLess fontSize="small" /> Hide
                                  </>
                                ) : (
                                  <>
                                    <ExpandMore fontSize="small" /> View
                                  </>
                                )}
                              </span>
                            </div>

                            <Typography
                              variant="caption"
                              className="text-muted"
                              style={{ display: "block", marginTop: 2 }}
                            >
                              List ID: {r.listId}
                            </Typography>
                          </div>

                          <div style={{ flex: "0 0 22%", textAlign: "center" }}>
                            ({r.count})
                          </div>

                          <div
                            style={{
                              flex: "0 0 20%",
                              display: "flex",
                              gap: 12,
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <EditIcon
                              fontSize="small"
                              style={{ cursor: "pointer" }}
                              titleAccess="Edit"
                              onClick={() => goToEditor(r.listId)}
                            />

                            <DeleteIcon
                              fontSize="small"
                              color="error"
                              style={{ cursor: "pointer" }}
                              titleAccess="Delete"
                              onClick={() => openDeleteConfirm(r)}
                            />
                          </div>
                        </div>

                        {/* Expanded Mapping Preview */}
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <div
                            style={{
                              background: "#fafafa",
                              padding: "14px 12px",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            <Typography
                              variant="body2"
                              style={{ fontWeight: 600 }}
                            >
                              Mapped Fields
                            </Typography>

                            <div
                              style={{
                                marginTop: 10,
                                border: "1px solid #e6e6e6",
                                borderRadius: 6,
                                overflow: "hidden",
                                background: "#fff",
                              }}
                            >
                              {/* inner header */}
                              <div
                                style={{
                                  display: "flex",
                                  padding: "10px 12px",
                                  fontWeight: 600,
                                  borderBottom: "1px solid #eee",
                                }}
                              >
                                <div style={{ width: "60%", minWidth: "60%" }}>
                                  <Typography variant="body1">
                                    <img
                                      className="mr-1  mb-1"
                                      src={CcbIcon}
                                      alt={"ccb-icon"}
                                    />{" "}
                                    Pushpay/CCB Custom Field
                                  </Typography>
                                </div>
                                <div style={{ width: "40%", minWidth: "40%" }}>
                                  <Typography variant="body1">
                                    <img
                                      className="mr-1 mb-1"
                                      src={MailchimpIcon}
                                      alt={"mc-icon"}
                                    />{" "}
                                    Mailchimp Merge Field/Tag
                                  </Typography>
                                </div>
                              </div>

                              {r.mappings.map((m, idx) => (
                                <div
                                  key={m?.id || `${r.listId}-${idx}`}
                                  style={{
                                    display: "flex",
                                    padding: "10px 12px",
                                    borderBottom:
                                      idx === r.mappings.length - 1
                                        ? "none"
                                        : "1px solid #f3f3f3",
                                    alignItems: "center",
                                    gap: 12,
                                  }}
                                >
                                  <div
                                    style={{ width: "60%", minWidth: "60%" }}
                                  >
                                    <Typography
                                      variant="body2"
                                      style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                      title={m?.ccbField || ""}
                                    >
                                      {m?.ccbField || "-"}
                                    </Typography>
                                  </div>

                                  <div
                                    style={{ width: "40%", minWidth: "40%" }}
                                  >
                                    <Typography
                                      variant="body2"
                                      style={{
                                        fontFamily:
                                          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                      title={m?.mailchimpField || ""}
                                    >
                                      {m?.mailchimpField || "-"}
                                    </Typography>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </Collapse>
                      </div>
                    );
                  })}
                </div>

                {/* Add more merge fields */}
                <div className="mt-4 flexer mb-4">
                  <MaterialButton
                    title={
                      <span>
                        <Add /> Add More Merge Fields
                      </span>
                    }
                    variant="contained"
                    className="bg-yellow w-25"
                    onClick={goToAddMore}
                  />
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={Boolean(deleteTarget?.listId)}
        handleClose={closeDeleteConfirm}
        modalHeading="Remove Custom Header Mapping?"
        modalContent={
          deleteTarget?.audienceName
            ? `This will remove the custom header mappings for "${deleteTarget.audienceName}" and stop future syncing for these fields. No existing data will be deleted from Mailchimp.`
            : "This will remove the custom header mappings and stop future syncing for these fields. No existing data will be deleted from Mailchimp."
        }
        btnLabel="Remove"
        btnClassName="bg-danger w-30 mr-3"
        showCancelBtn={true}
        cancelBtnLabel="Cancel"
        loading={isDeleting}
        disabled={isDeleting}
        onClick={confirmDelete}
      />
    </>
  );
};

const mapStateToProps = (store) => {
  return {
    audienceWithCustomHeaderMappings:
      store.tools.audienceWithCustomHeaderMappings,
    headerMappingStatus: store.tools.headerMappingStatus,
    deleteCustomHeaderMappingStatus:
      store.tools.deleteCustomHeaderMappingStatus,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getHeaderMapping: (formData) => dispatch(getHeaderMapping(formData)),
    deleteCustomHeaderMappings: (formData) =>
      dispatch(deleteCustomHeaderMappings(formData)),
    clearDeleteCustomMCHeaderMapping: (formData) =>
      dispatch(clearDeleteCustomMCHeaderMapping(formData)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(CustomMailchimpHeaderMappingOverviewModal));
